/* eslint-disable no-unused-vars */
// src/components/Gehaltsrechner.js
import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { AuthContext } from '../contexts/AuthContext';
import { API_URL } from './apiurl';

const Gehaltsrechner = () => {
  const { auth } = useContext(AuthContext);

  // Initialer State, inkl. Berechnungsjahr und -monat (aktueller Monat als Zahl 1–12)
  const [formData, setFormData] = useState({
    monthlyGrossIncome: 3250,
    incomeTaxClass: '1',
    childAllowance: 0,
    taxExemptAmountMonthly: 0,
    state: 'sh', // z. B. Schleswig-Holstein
    churchTax: true,
    hasChildren: false,
    childrenBelow25: 0,
    calculationYear: new Date().getFullYear(), // Standard: aktuelles Jahr
    calculationMonth: new Date().getMonth() + 1,  // Standard: aktueller Monat (1-12)
    insuranceType: 'compulsory',
    privateHealthInsurancePremium: 0,
    provision: 0,
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [m_bonus, setBonus] = useState(0.0);

  // Handler für Änderungen in den Formularfeldern
  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [id]: checked,
        ...(id === 'hasChildren' && !checked ? { childrenBelow25: 0 } : {}),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: type === 'number' ? parseFloat(value) : value,
      }));
    }
  };

  // Gehalt-Daten abrufen – Jahr und Monat werden als Query-Parameter übergeben
  async function getGehalt() {
    try {
      const { calculationYear, calculationMonth } = formData;
      const response = await fetch(
        `${API_URL}/gehalt?year=${calculationYear}&month=${calculationMonth}`,
        {
          headers: {
            'Authorization': `Bearer ${auth.token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Fehler beim Abrufen der Gehaltsdaten: ${response.statusText}`);
      }
      const data = await response.json();
      const { istUmsatz, sollUmsatz, bonus, bruttoFixum } = data;

      setBonus(bonus);
      setFormData((prevData) => ({
        ...prevData,
        provision: bonus,
        monthlyGrossIncome: bruttoFixum || 0,
      }));
    } catch (error) {
      //console.error('Fehler beim Abrufen des Gehalts:', error);
      toast.error('Fehler beim Abrufen des Gehalts.');
    }
  }

  // Hole Gehalt-Daten neu, wenn sich Jahr oder Monat ändern
  useEffect(() => {
    getGehalt();
  }, [formData.calculationYear, formData.calculationMonth]);

  // Berechnung der Sozialversicherungsbeiträge (Beispielrechnung)
  const calculateSocialSecurity = (monthlyGrossIncome, options, taxYear) => {
    // Wir ignorieren den Parameter taxYear hier und verwenden
    // immer dieselben Werte (z. B. die 2025‑Werte).
    const { state, hasChildren, insuranceType } = options;
    const mg = parseFloat(monthlyGrossIncome);
  
    // Fest definierte Werte – hier verwenden wir die 2025‑Werte als "Common" Werte
    const rates = {
      healthInsurance: {
        rate: 0.146,
        additionalSurcharge: 0.025,
        contributionBase: 5512.5,
      },
      nursingCareInsurance: {
        rate: 0.036,
        additionalSurcharge: 0.006,
        contributionBase: 5512.5,
      },
      pensionInsurance: {
        rate: 0.186,
        contributionBase: 8050,
      },
      unemploymentInsurance: {
        rate: 0.026,
        contributionBase: 8050,
      },
    };
  
    // Rentenversicherung
    const pensionBase = parseFloat(rates.pensionInsurance.contributionBase);
    const pensionApplicable = Math.min(mg, pensionBase);
    const pensionInsurance = pensionApplicable * rates.pensionInsurance.rate * 0.5;
  
    // Arbeitslosenversicherung
    const unemploymentBase = parseFloat(rates.unemploymentInsurance.contributionBase);
    const unemploymentApplicable = Math.min(mg, unemploymentBase);
    const unemploymentInsurance = unemploymentApplicable * rates.unemploymentInsurance.rate * 0.5;
  
    // Krankenversicherung
    const healthBase = Math.min(mg, parseFloat(rates.healthInsurance.contributionBase));
    const healthInsurance = healthBase * rates.healthInsurance.rate * 0.5;
    const healthAdditionalSurcharge = healthBase * rates.healthInsurance.additionalSurcharge * 0.5;
    const totalHealthInsurance = healthInsurance + healthAdditionalSurcharge;
  
    // Pflegeversicherung
    const nursingCareBase = Math.min(mg, parseFloat(rates.nursingCareInsurance.contributionBase));
    // Falls Du spezielle Regelungen (z. B. für Sachsen bei kinderlosen Arbeitnehmern) ignorieren möchtest, 
    // berechnen wir hier einfach immer den Standardwert.
    const nursingCareInsurance = nursingCareBase * rates.nursingCareInsurance.rate * 0.5 +
                                 nursingCareBase * rates.nursingCareInsurance.additionalSurcharge * 0.5;
  
    const totalSocialSecurity = pensionInsurance + unemploymentInsurance + totalHealthInsurance + nursingCareInsurance;
  
    return {
      pensionInsurance: pensionInsurance.toFixed(2),
      unemploymentInsurance: unemploymentInsurance.toFixed(2),
      healthInsurance: totalHealthInsurance.toFixed(2),
      nursingCareInsurance: nursingCareInsurance.toFixed(2),
      totalSocialSecurity: totalSocialSecurity.toFixed(2),
    };
  };
  
  
  

  // Netto-Gehalt berechnen (unter anderem durch Aufruf des /calculate-tax Endpoints)
  const calculateNetSalary = async (data) => {
    const {
      monthlyGrossIncome,
      incomeTaxClass,
      childAllowance,
      taxExemptAmountMonthly,
      state,
      churchTax,
      hasChildren,
      childrenBelow25,
      calculationYear,
      insuranceType,
      privateHealthInsurancePremium,
      provision,
    } = data;

    try {
      setLoading(true);

      const updatedMonthlyGrossIncome = parseFloat(monthlyGrossIncome) + parseFloat(provision);
      const RE4 = Math.round(updatedMonthlyGrossIncome * 100);
      const LZZ = 2; // monatliche Berechnung
      const STKL = parseInt(incomeTaxClass);

      const { calculationYear, calculationMonth } = formData
      //var code_ = 'extS' + calculationYear;
      const requestBody = {
        code: 'extS2025',
        LZZ,
        RE4,
        STKL,
      };

      const response = await fetch(`${API_URL}/calculate-tax`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Fehler bei der Steuerberechnung: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const steuerdaten = await response.json();
      const LSTLZZ = parseFloat(steuerdaten.LSTLZZ) || 0;
      const SOLZLZZ = parseFloat(steuerdaten.SOLZLZZ) || 0;
      const incomeTax = LSTLZZ;
      const solidaritySurcharge = SOLZLZZ;

      const calculateChurchTax = (incomeTax, churchTax, state) => {
        if (!churchTax) return 0;
        const churchTaxRates = {
          bw: 0.09,
          by: 0.09,
          be: 0.09,
          bb: 0.09,
          hb: 0.09,
          hh: 0.09,
          he: 0.09,
          mv: 0.09,
          ni: 0.09,
          nw: 0.09,
          rp: 0.09,
          sl: 0.09,
          sn: 0.09,
          st: 0.09,
          sh: 0.09,
          th: 0.09,
        };
        const rate = churchTaxRates[state] || 0.08;
        return incomeTax * rate;
      };

      const churchTaxValue = calculateChurchTax(incomeTax, churchTax, state);
      const socialSecurity = calculateSocialSecurity(
        updatedMonthlyGrossIncome,
        { state, hasChildren, insuranceType },
        calculationYear
      );
      const totalSocialSecurity = parseFloat(socialSecurity.totalSocialSecurity);
      const privateHealthInsurance = insuranceType === 'private' ? parseFloat(privateHealthInsurancePremium) : 0;

      const netSalary =
        updatedMonthlyGrossIncome -
        totalSocialSecurity -
        incomeTax -
        solidaritySurcharge -
        churchTaxValue -
        privateHealthInsurance;


      setResult({
        pensionInsurance: socialSecurity.pensionInsurance,
        unemploymentInsurance: socialSecurity.unemploymentInsurance,
        healthInsurance: socialSecurity.healthInsurance,
        nursingCareInsurance: socialSecurity.nursingCareInsurance,
        incomeTax: incomeTax.toFixed(2),
        solidaritySurcharge: solidaritySurcharge.toFixed(2),
        churchTax: churchTaxValue.toFixed(2),
        privateHealthInsurance: privateHealthInsurance.toFixed(2),
        totalSocialSecurity: totalSocialSecurity.toFixed(2),
        netSalary: netSalary.toFixed(2),
      });
      toast.success('Berechnung erfolgreich!');
    } catch (error) {
      toast.error('Fehler bei der Berechnung.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    await calculateNetSalary(formData);
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Brutto-Netto-Rechner</h3>
      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Bruttogehalt */}
        <div style={styles.formGroup}>
          <label htmlFor="monthlyGrossIncome" style={styles.label}>Bruttogehalt (€)</label>
          <input
            id="monthlyGrossIncome"
            type="number"
            step="0.01"
            min="0.01"
            required
            style={styles.input}
            value={formData.monthlyGrossIncome}
            onChange={handleChange}
          />
        </div>
        {/* Provision/Bonus */}
        <div style={styles.formGroup}>
          <label htmlFor="provision" style={styles.label}>Provision (€)</label>
          <input
            id="provision"
            type="number"
            step="0.01"
            style={styles.input}
            value={formData.provision}
            onChange={handleChange}
          />
        </div>
        {/* Berechnungsjahr */}
        <div style={styles.formGroup}>
          <label htmlFor="calculationYear" style={styles.label}>Berechnungsjahr</label>
          <select
            id="calculationYear"
            required
            style={styles.select}
            value={formData.calculationYear}
            onChange={handleChange}
          >
            <option value={2024}>2024</option>
            <option value={2025}>2025</option>
          </select>
        </div>
        {/* Berechnungsmonat */}
        <div style={styles.formGroup}>
          <label htmlFor="calculationMonth" style={styles.label}>Berechnungsmonat</label>
          <select
            id="calculationMonth"
            required
            style={styles.select}
            value={formData.calculationMonth}
            onChange={handleChange}
          >
            <option value={1}>Januar</option>
            <option value={2}>Februar</option>
            <option value={3}>März</option>
            <option value={4}>April</option>
            <option value={5}>Mai</option>
            <option value={6}>Juni</option>
            <option value={7}>Juli</option>
            <option value={8}>August</option>
            <option value={9}>September</option>
            <option value={10}>Oktober</option>
            <option value={11}>November</option>
            <option value={12}>Dezember</option>
          </select>
        </div>
        {/* Steuerklasse */}
        <div style={styles.formGroup}>
          <label htmlFor="incomeTaxClass" style={styles.label}>Steuerklasse</label>
          <select
            id="incomeTaxClass"
            required
            style={styles.select}
            value={formData.incomeTaxClass}
            onChange={handleChange}
          >
            <option value="">Steuerklasse wählen</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
          </select>
        </div>
        {/* Kinderfreibetrag */}
        <div style={styles.formGroup}>
          <label htmlFor="childAllowance" style={styles.label}>Kinderfreibetrag</label>
          <select
            id="childAllowance"
            required
            style={styles.select}
            value={formData.childAllowance}
            onChange={handleChange}
          >
            {[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9].map((val, idx) => (
              <option key={idx} value={val}>{val}</option>
            ))}
          </select>
        </div>
        {/* Steuerfreibetrag */}
        <div style={styles.formGroup}>
          <label htmlFor="taxExemptAmountMonthly" style={styles.label}>Steuerfreibetrag (monatlich, €)</label>
          <input
            id="taxExemptAmountMonthly"
            type="number"
            step="0.01"
            min="0"
            required
            style={styles.input}
            value={formData.taxExemptAmountMonthly}
            onChange={handleChange}
          />
        </div>
        {/* Bundesland */}
        <div style={styles.formGroup}>
          <label htmlFor="state" style={styles.label}>Bundesland</label>
          <select
            id="state"
            required
            style={styles.select}
            value={formData.state}
            onChange={handleChange}
          >
            <option value="bw">Baden-Württemberg</option>
            <option value="by">Bayern</option>
            <option value="be">Berlin</option>
            <option value="bb">Brandenburg</option>
            <option value="hb">Bremen</option>
            <option value="hh">Hamburg</option>
            <option value="he">Hessen</option>
            <option value="mv">Mecklenburg-Vorpommern</option>
            <option value="ni">Niedersachsen</option>
            <option value="nw">Nordrhein-Westfalen</option>
            <option value="rp">Rheinland-Pfalz</option>
            <option value="sl">Saarland</option>
            <option value="sn">Sachsen</option>
            <option value="st">Sachsen-Anhalt</option>
            <option value="sh">Schleswig-Holstein</option>
            <option value="th">Thüringen</option>
          </select>
        </div>
        {/* Kirchensteuer */}
        <div style={styles.checkboxGroup}>
          <input
            type="checkbox"
            id="churchTax"
            checked={formData.churchTax}
            onChange={handleChange}
            style={styles.checkbox}
          />
          <label htmlFor="churchTax" style={styles.checkboxLabel}>Kirchensteuer</label>
        </div>
        {/* Hat Kinder */}
        <div style={styles.checkboxGroup}>
          <input
            type="checkbox"
            id="hasChildren"
            checked={formData.hasChildren}
            onChange={handleChange}
            style={styles.checkbox}
          />
          <label htmlFor="hasChildren" style={styles.checkboxLabel}>Hat Kinder</label>
        </div>
        {/* Anzahl Kinder unter 25 */}
        {formData.hasChildren && (
          <div style={styles.formGroup}>
            <label htmlFor="childrenBelow25" style={styles.label}>Anzahl Kinder unter 25</label>
            <select
              id="childrenBelow25"
              required
              style={styles.select}
              value={formData.childrenBelow25}
              onChange={handleChange}
            >
              <option value={0}>0</option>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5 und mehr</option>
            </select>
          </div>
        )}
        {/* Art der Versicherung */}
        <div style={styles.formGroup}>
          <label htmlFor="insuranceType" style={styles.label}>Art der Versicherung</label>
          <select
            id="insuranceType"
            required
            style={styles.select}
            value={formData.insuranceType}
            onChange={handleChange}
          >
            <option value="compulsory">Gesetzlich</option>
            <option value="private">Privat</option>
          </select>
        </div>
        {/* Private Krankenversicherung */}
        {formData.insuranceType === 'private' && (
          <div style={styles.formGroup}>
            <label htmlFor="privateHealthInsurancePremium" style={styles.label}>PKV Beitrag (€)</label>
            <input
              id="privateHealthInsurancePremium"
              type="number"
              step="0.01"
              min="0"
              required
              style={styles.input}
              value={formData.privateHealthInsurancePremium}
              onChange={handleChange}
            />
          </div>
        )}
        {/* Submit Button */}
        <button
          type="submit"
          style={{
            ...styles.submitButton,
            ...(loading ? styles.submitButtonDisabled : {}),
          }}
          disabled={loading}
        >
          {loading ? 'Berechnung läuft...' : 'Berechnen'}
        </button>
      </form>

      {/* Ergebnis anzeigen */}
      {result && (
        <div style={styles.result}>
          <h3>Ergebnis:</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Kategorie</th>
                <th style={styles.th}>Beschreibung</th>
                <th style={styles.th}>Betrag (€)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.td}>Sozialversicherung</td>
                <td style={styles.td}>Rentenversicherung</td>
                <td style={styles.td}>{result.pensionInsurance}</td>
              </tr>
              <tr>
                <td style={styles.td}></td>
                <td style={styles.td}>Arbeitslosenversicherung</td>
                <td style={styles.td}>{result.unemploymentInsurance}</td>
              </tr>
              <tr>
                <td style={styles.td}></td>
                <td style={styles.td}>Krankenversicherung</td>
                <td style={styles.td}>{result.healthInsurance}</td>
              </tr>
              <tr>
                <td style={styles.td}></td>
                <td style={styles.td}>Pflegeversicherung</td>
                <td style={styles.td}>{result.nursingCareInsurance}</td>
              </tr>
              <tr>
                <td style={styles.td}>Steuern</td>
                <td style={styles.td}>Einkommensteuer</td>
                <td style={styles.td}>{result.incomeTax}</td>
              </tr>
              <tr>
                <td style={styles.td}></td>
                <td style={styles.td}>Solidaritätszuschlag</td>
                <td style={styles.td}>{result.solidaritySurcharge}</td>
              </tr>
              <tr>
                <td style={styles.td}></td>
                <td style={styles.td}>Kirchensteuer</td>
                <td style={styles.td}>{result.churchTax}</td>
              </tr>
              {formData.insuranceType === 'private' && (
                <tr>
                  <td style={styles.td}>Private Krankenversicherung</td>
                  <td style={styles.td}>Beitrag</td>
                  <td style={styles.td}>{result.privateHealthInsurance}</td>
                </tr>
              )}
              <tr>
                <td style={styles.td}>Netto-Gehalt</td>
                <td style={styles.td}></td>
                <td style={styles.td}>{result.netSalary}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '1.5rem',
    backgroundColor: '#e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
    fontFamily: 'Roboto, sans-serif',
  },
  title: {
    textAlign: 'center',
    marginBottom: '1.5rem',
    fontSize: '1.5rem',
    color: '#374151',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    marginBottom: '0.5rem',
    color: '#4b5563',
    fontWeight: '500',
  },
  input: {
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '1rem',
    color: '#374151',
  },
  select: {
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '1rem',
    color: '#374151',
    backgroundColor: '#fff',
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  checkbox: {
    marginRight: '0.5rem',
  },
  checkboxLabel: {
    color: '#4b5563',
    fontWeight: '500',
  },
  submitButton: {
    padding: '0.75rem',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'background-color 0.3s',
  },
  submitButtonDisabled: {
    backgroundColor: '#93c5fd',
    cursor: 'not-allowed',
  },
  result: {
    marginTop: '1.5rem',
    padding: '1rem',
    backgroundColor: '#d1d5db',
    borderRadius: '6px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '0.5rem',
    border: '1px solid #9ca3af',
    backgroundColor: '#6b7280',
    color: '#fff',
    textAlign: 'left',
  },
  td: {
    padding: '0.5rem',
    border: '1px solid #9ca3af',
    backgroundColor: '#f3f4f6',
  },
};

export default Gehaltsrechner;
